import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdAddchart } from 'react-icons/md';
import { IoIosSearch } from 'react-icons/io';
import { toast } from '../../toasts';
import { useLabels } from '../../hooks/useLabels';
import { getError } from '../../utils';
import AccountService from '../../services/account.service';
import { EntityList, PageHead, saveBlob } from '../../components/EntityList';
import ReportCatalog from './ReportCatalog';
import ReportConfigPanel from './ReportConfigPanel';
import { getReport, seedFilterOptions, clearFilterOptions } from './reportsCatalogData';
import { generateReportFile } from './reportFileBuilder';
import ReportService from '../../services/report.service';
import GeneratedReportsTable from './GeneratedReportsTable';

const ACCOUNTS_LIST_FORM_TYPES = ['VentasActivo', 'ActivosFijos', 'EstadoCuenta'];
const HEAVY_REPORT_TYPES = {
	'Estado Situación': 'estado_situacion',
	'Últimos pagos por cuota de deuda': 'ultimos_pagos_cuota_deuda',
};

function Reports() {
	const { t } = useLabels();

	const { search } = useLocation();

	const navigate = useNavigate();
	const sp = new URLSearchParams(search);
	let page = sp.get('page') || 1;
	const concept = sp.get('concept') || 'all';
	const priceFrom = sp.get('priceFrom') || 'all';
	const priceTo = sp.get('priceTo') || 'all';
	const name = sp.get('name') || '';
	const ubication = sp.get('ubication') || '';
	const owner = sp.get('owner') || '';
	const purchaser = sp.get('purchaser') || '';
	const sold = sp.get('sold') || '';
	const lot = sp.get('lot') || '';
	const currency = sp.get('currency') || 'all';
	const capitalTo = sp.get('capitalTo') || '';
	const capitalFrom = sp.get('capitalFrom') || '';
	const amountTo = sp.get('amountTo') || '';
	const amountFrom = sp.get('amountFrom') || 0;
	const dateFrom = sp.get('dateFrom') || '';
	const dateTo = sp.get('dateTo') || '';
	const endDateFrom = sp.get('endDateFrom') || '';
	const endDateTo = sp.get('endDateTo') || '';
	const query = sp.get('query') || 'all';
	const periodInitFrom = sp.get('periodInitFrom') || '';
	const periodInitTo = sp.get('periodInitTo') || '';
	const periodEndFrom = sp.get('periodEndFrom') || '';
	const periodEndTo = sp.get('periodEndTo') || '';
	const accountId = sp.get('accountId') || '';
	const lastPaymentFrom =sp.get('lastPaymentFrom') || '';
	const lastPaymentTo =sp.get('lastPaymentTo') || '';
	const reportIdFromUrl = sp.get('reportId') || null;
	const [accountsList, setAccountsList] = useState([]);

	const urlValues = {
		concept, priceFrom, priceTo, name, ubication, owner, purchaser, sold, lot,
		currency, capitalTo, capitalFrom, amountTo, amountFrom, dateFrom, dateTo,
		endDateFrom, endDateTo, query, periodInitFrom, periodInitTo, periodEndFrom,
		periodEndTo, accountId, lastPaymentFrom, lastPaymentTo, page,
	};

	const [selectedId, setSelectedId] = useState(reportIdFromUrl);
	const [catalogQuery, setCatalogQuery] = useState('');
	const [filterOptions, setFilterOptions] = useState({});
	const [genState, setGenState] = useState('idle');
	const [genResult, setGenResult] = useState(null);
	const [heavyReports, setHeavyReports] = useState([]);
	const [loadingHeavyReports, setLoadingHeavyReports] = useState(false);

	const report = getReport(selectedId);

	// Mount-only hydration: seed filters from the URL, but never restart a
	// background report automatically after a refresh.
	const hydrated = useRef(false);
	useEffect(() => {
		if (hydrated.current) return;
		hydrated.current = true;
		if (reportIdFromUrl) {
			const initialReport = getReport(reportIdFromUrl);
			if (initialReport) {
				setFilterOptions(seedFilterOptions(initialReport, urlValues));
			}
		}
	}, []);

	const loadHeavyReports = useCallback(async ({ silent = false } = {}) => {
		if (!silent) setLoadingHeavyReports(true);
		try {
			const { data } = await ReportService.listHeavy();
			setHeavyReports(data);
		} catch (err) {
			if (!silent) toast.error(getError(err));
		} finally {
			if (!silent) setLoadingHeavyReports(false);
		}
	}, []);

	useEffect(() => {
		loadHeavyReports();
	}, [loadHeavyReports]);

	const heavyReportBusy = heavyReports.some((item) => item.status === 'in_progress');
	useEffect(() => {
		if (!heavyReportBusy) return undefined;
		const timer = setInterval(() => loadHeavyReports({ silent: true }), 15000);
		return () => clearInterval(timer);
	}, [heavyReportBusy, loadHeavyReports]);

	// Populates the owner/purchaser/account pickers as soon as a report needing
	// them is selected, rather than only after generation — VentasActivo and
	// ActivosFijos never re-run the fetch effect below since they no longer
	// route through it.
	useEffect(() => {
		if (!ACCOUNTS_LIST_FORM_TYPES.includes(filterOptions.formType)) return;
		let cancelled = false;
		AccountService.list(true).then(({ data }) => {
			if (cancelled) return;
			setAccountsList(data.map((account) => ({ value: account._id, label: account.name })));
		});
		return () => {
			cancelled = true;
		};
	}, [filterOptions.formType]);

	function handleSelectReport(id) {
		const nextReport = getReport(id);
		if (!nextReport) return;
		setSelectedId(id);
		setGenState('idle');
		setGenResult(null);
		setFilterOptions(seedFilterOptions(nextReport, urlValues));
	}

	function handleClosePanel() {
		setSelectedId(null);
		setGenState('idle');
		setGenResult(null);
		navigate('/AdminScreen/reports');
	}

	const handleClearFilter = () => {
		if (!report) return;
		setFilterOptions(clearFilterOptions(report));
	};

	const handleInputChange = (eOrDate, maybeName) => {
		let name, value;

		if (eOrDate?.target) {
			({ name, value } = eOrDate.target);
		} else {
			name = maybeName;
			value = eOrDate || null;
		}

		setFilterOptions((prev) => ({
			...prev,
			[name]: value,
			formType: prev.formType,
		}));
	};

	// The two heavy reports enqueue a backend job; the other reports continue
	// generating their XLSX immediately in the browser.
	async function handleGenerate() {
		if (!report) return;
		const heavyReportType = HEAVY_REPORT_TYPES[report.id];
		if (heavyReportType) {
			setGenState('preparing');
			try {
				const { data } = await ReportService.createHeavy(heavyReportType, filterOptions);
				toast.success(data.message);
				await loadHeavyReports({ silent: true });
			} catch (err) {
				toast.error(getError(err));
			} finally {
				setGenState('idle');
			}
			return;
		}
		setGenState('preparing');
		try {
			const result = await generateReportFile(report, filterOptions);
			setGenResult(result);
			setGenState('ready');
		} catch (err) {
			toast.error(getError(err));
			setGenState('idle');
		}
	}

	function handleBackToIdle() {
		setGenState('idle');
	}

	function handleDownloadReport() {
		if (genResult) saveBlob(genResult.blob, genResult.filename);
	}

	async function handleDownloadHeavy(report) {
		try {
			const { data } = await ReportService.downloadHeavy(report._id);
			window.location.assign(data.url);
		} catch (err) {
			toast.error(getError(err));
		}
	}

	const filterFormProps = {
		filterOptions,
		handleInputChange,
		clearFilter: handleClearFilter,
		handleApplyFilter: handleGenerate,
		accountsList: accountsList ? accountsList : '',
	};

	return (
		<EntityList>
			<PageHead
				breadcrumb={[{ label: 'Inicio', to: '/AdminScreen' }, { label: t('entity.report.plural') }]}
				title={
					<span className='tw:inline-flex tw:items-center tw:gap-3'>
						<span className='tw:grid tw:h-[38px] tw:w-[38px] tw:place-items-center tw:rounded-[10px] tw:bg-accent-soft tw:text-accent-ink'>
							<MdAddchart size={20} />
						</span>
						{`Generación de ${t('entity.report.plural')}`}
					</span>
				}
				subtitle='Elegí un reporte del catálogo, configurá los filtros y generalo.'
			/>

			<div className='tw:grid tw:grid-cols-1 tw:items-start tw:gap-6 tw:min-[1080px]:grid-cols-[1fr_360px]'>
				<ReportCatalog
					query={catalogQuery}
					selectedId={selectedId}
					onSelect={handleSelectReport}
				/>
				<div className='tw:flex tw:flex-col tw:gap-3 tw:min-[1080px]:sticky tw:min-[1080px]:top-6'>
					<div className='tw:flex tw:h-[34px] tw:items-center tw:gap-2 tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:text-ink-2'>
						<IoIosSearch className='tw:shrink-0 tw:text-[15px]' />
						<input
							type='text'
							value={catalogQuery}
							onChange={(e) => setCatalogQuery(e.target.value)}
							placeholder='Buscar reporte por nombre'
							aria-label='Buscar reporte'
							className='tw:w-full tw:border-0 tw:bg-transparent tw:text-[13px] tw:text-ink-0 tw:outline-none placeholder:tw:text-ink-3'
						/>
					</div>
					<ReportConfigPanel
						report={report}
						filterFormProps={filterFormProps}
						onClose={handleClosePanel}
						onGenerate={handleGenerate}
						genState={genState}
						result={genResult}
						onBack={handleBackToIdle}
						onDownload={handleDownloadReport}
						isHeavyReport={Boolean(report && HEAVY_REPORT_TYPES[report.id])}
						heavyReportBusy={heavyReportBusy}
					/>
				</div>
			</div>

			<GeneratedReportsTable
				reports={heavyReports}
				loading={loadingHeavyReports}
				onRefresh={loadHeavyReports}
				onDownload={handleDownloadHeavy}
			/>
		</EntityList>
	);
}
export default Reports;
